/**
 * A version is a name on a set of heads (RFC 0313 3c).
 *
 * IT STORES NO CONTENT. Automerge keeps everything a set of heads points at, so
 * a version of a whole project is a few hundred hashes, and reading one back is
 * `A.view` at those heads. A version never changes: going back to one is a new
 * change (restore, 3e) or a new branch (3j), never a rewind.
 *
 * ONE DOCUMENT PER PROJECT holds them, pointed to from `ProjectDoc`, so drawing
 * the sidebar does not load five hundred of them.
 */

import type { AnyDocumentId, Repo } from '@automerge/automerge-repo/slim'
import { Automerge as A } from '@automerge/automerge-repo/slim'
import { catalogDocument } from '../documents/mutations'
import { projectDocuments } from '../documents/reads'
import { changeProject, projectString } from '../project/scalars'

export type VersionCovers =
  | { kind: 'document'; documentId: string }
  | { kind: 'app'; appId: string }
  | { kind: 'project' }

/** A named point in history (3c). Kept in the project's versions document. */
export interface Version {
  id: string
  name: string
  note?: string
  /** A person's `userDocId`, a session id, or "auto" for one Mod saved itself. */
  by: string
  at: number
  covers: VersionCovers
  /** Each covered document's id, to its heads at that moment. */
  heads: Record<string, string[]>
  /** The captured code state, when there was one. */
  commit?: string
  /** Set on a version Mod saved itself, naming why: a merge's or an install's id. */
  opId?: string
}

export interface VersionsDoc {
  versions: Record<string, Version>
}

async function ready<T>(repo: Repo, id: string) {
  const h = await repo.find<T>(id as AnyDocumentId)
  await (h as { whenReady?: () => Promise<void> }).whenReady?.()
  return h
}

/** The project's versions document, made the first time it is needed. */
export async function versionsDocId(
  repo: Repo,
  projectId: string,
): Promise<string> {
  const project = await ready<{ versionsDocId?: string }>(repo, projectId)
  const existing = projectString(project.doc()?.versionsDocId)
  if (existing) return existing
  const created = repo.create<VersionsDoc>({ versions: {} })
  const id = created.documentId as string
  changeProject(project, (p) => {
    // A second device racing to make one: the first write wins and the other
    // copy is simply never pointed at.
    if (!p.versionsDocId) p.versionsDocId = id
  })
  return projectString(project.doc()?.versionsDocId) ?? id
}

/** Every document an app version covers: its record, view, skill and source. */
export async function appDocumentIds(
  repo: Repo,
  appId: string,
): Promise<string[]> {
  const app = await ready<Record<string, unknown>>(repo, appId)
  const d = app.doc() ?? {}
  const ids = [appId]
  for (const k of ['viewDocId', 'skillDocId', 'sourceDocId']) {
    const v = d[k]
    if (typeof v === 'string' && v) ids.push(v)
  }
  const sourceDocId = typeof d.sourceDocId === 'string' ? d.sourceDocId : ''
  if (sourceDocId) {
    try {
      const manifest = await ready<{ docs?: { id: string }[] }>(
        repo,
        sourceDocId,
      )
      for (const f of manifest.doc()?.docs ?? []) {
        if (typeof f.id === 'string') ids.push(f.id)
      }
    } catch {
      // A manifest this copy cannot read: the version still names the rest.
    }
  }
  return [...new Set(ids)]
}

async function coveredIds(
  repo: Repo,
  projectId: string,
  covers: VersionCovers,
): Promise<string[]> {
  if (covers.kind === 'document') return [covers.documentId]
  if (covers.kind === 'app') return appDocumentIds(repo, covers.appId)
  const project = await ready(repo, projectId)
  return projectDocuments(project.doc()).map((d) => d.id)
}

/** Save a version of a document, an app or a project, and return it. */
export async function saveVersion(
  repo: Repo,
  projectId: string,
  input: {
    name: string
    note?: string
    by: string
    covers: VersionCovers
    commit?: string
    opId?: string
  },
): Promise<Version> {
  // An automatic version for an operation already saved one: a retry, so the
  // one that exists is the answer (3l).
  if (input.opId) {
    const found = (await listVersions(repo, projectId)).find(
      (v) => v.opId === input.opId,
    )
    if (found) return found
  }
  const heads: Record<string, string[]> = {}
  for (const id of await coveredIds(repo, projectId, input.covers)) {
    try {
      const h = await ready(repo, id)
      const doc = h.doc() as A.Doc<unknown> | undefined
      if (doc) heads[id] = A.getHeads(doc)
    } catch {
      // A document this copy cannot open is not in the version, and says so by
      // its absence rather than by failing the whole save.
    }
  }
  const version: Version = {
    id: crypto.randomUUID(),
    name: input.name,
    by: input.by,
    at: Date.now(),
    covers: input.covers,
    heads,
    ...(input.note ? { note: input.note } : {}),
    ...(input.commit ? { commit: input.commit } : {}),
    ...(input.opId ? { opId: input.opId } : {}),
  }
  const store = await ready<VersionsDoc>(
    repo,
    await versionsDocId(repo, projectId),
  )
  store.change((d) => {
    if (!d.versions) d.versions = {}
    d.versions[version.id] = version
  })
  return version
}

/** A project's versions, newest first. */
export async function listVersions(
  repo: Repo,
  projectId: string,
): Promise<Version[]> {
  const project = await ready<{ versionsDocId?: string }>(repo, projectId)
  const id = projectString(project.doc()?.versionsDocId)
  if (!id) return []
  return readVersions((await ready<VersionsDoc>(repo, id)).doc())
}

/** The versions in a versions document, newest first. Pure. */
export function readVersions(doc: unknown): Version[] {
  const versions = (doc as VersionsDoc | undefined)?.versions
  if (!versions || typeof versions !== 'object') return []
  return Object.values(
    JSON.parse(JSON.stringify(versions)) as Record<string, Version>,
  ).sort((a, b) => b.at - a.at)
}

/** The versions that hold this document's state, newest first. Pure. */
export function versionsOf(versions: Version[], documentId: string): Version[] {
  return versions.filter((v) => Array.isArray(v.heads[documentId]))
}

/**
 * Open a version of a document as a new document of its own (paired review of
 * PR #329): explore another direction without giving up the current one. The
 * copy carries the history it came from, up to the version, and sits beside the
 * original in the catalog. Returns its id; null when the version does not hold
 * the document.
 */
export async function copyFromVersion(
  repo: Repo,
  projectId: string,
  documentId: string,
  version: Version,
): Promise<string | null> {
  const heads = version.heads[documentId]
  if (!heads) return null
  const original = (await ready(repo, documentId)).doc() as A.Doc<unknown>
  const forked = A.clone(A.view(original, heads))
  const copy = repo.import(A.save(forked))
  const copyId = copy.documentId as string
  const project = await ready(repo, projectId)
  const entry = projectDocuments(project.doc()).find((d) => d.id === documentId)
  const title = `${entry?.title ?? 'Untitled'} (${version.name})`
  const path = copyPath(entry?.path ?? 'copy.md', version.name)
  copy.change((d: Record<string, unknown>) => {
    d.id = copyId
    d.path = path
    d.updatedAt = Date.now()
  })
  await catalogDocument(repo, projectId, copyId, {
    title,
    path,
    mime: entry?.mime ?? 'text/markdown',
    updatedAt: Date.now(),
    ...(entry?.dataType ? { dataType: entry.dataType } : {}),
  })
  return copyId
}

/** `notes/plan.md` + "v2" → `notes/plan-v2.md`. Pure. */
export function copyPath(path: string, name: string): string {
  const slug =
    name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '')
      .slice(0, 40) || 'copy'
  const dot = path.lastIndexOf('.')
  const slash = path.lastIndexOf('/')
  return dot > slash
    ? `${path.slice(0, dot)}-${slug}${path.slice(dot)}`
    : `${path}-${slug}`
}

/** A document as a version saved it. Null when the version does not hold it. */
export async function readAtVersion(
  repo: Repo,
  version: Version,
  documentId: string,
): Promise<unknown | null> {
  const heads = version.heads[documentId]
  if (!heads) return null
  const h = await ready(repo, documentId)
  return A.view(h.doc() as A.Doc<unknown>, heads)
}
